import React, { useEffect, useState } from "react";
import Tile from "@src/components/containers/Tile";
import { Input } from "@swordsweeper/ssi-ui-component-library";
import { getDeliveryModes } from "@src/api/CovetrusApi";
import { extractServerErrorMessage } from "@src/utils/error";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import styles from "./CovetrusOrderInfoForm.module.scss";

export interface CovetrusOrderInfo {
  deliveryModeCode: string;
  paymentType: "ACCOUNT" | "CARD";
  eSignature: string;
}

interface CovetrusOrderInfoFormProps {
  onChange: (info: CovetrusOrderInfo | null) => void;
}

interface DeliveryMode {
  code: string;
  name: string;
  description?: string;
}

function RadioOption({
  name,
  value,
  checked,
  label,
  description,
  onChange,
}: {
  name: string;
  value: string;
  checked: boolean;
  label: string;
  description?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className={styles.radioOption}>
      <input
        type="radio"
        className={styles.radio}
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
      />
      {description ? (
        <span className={styles.deliveryText}>
          <span className="text-sm font-weight-bold">{label}</span>
          <span className="text-xs text-secondary">{description}</span>
        </span>
      ) : (
        <span className="text-sm font-weight-bold">{label}</span>
      )}
    </label>
  );
}

export default function CovetrusOrderInfoForm({
  onChange,
}: CovetrusOrderInfoFormProps) {
  const [deliveryModes, setDeliveryModes] = useState<DeliveryMode[]>([]);
  const [deliveryModeCode, setDeliveryModeCode] = useState("");
  const [paymentType, setPaymentType] = useState<"ACCOUNT" | "CARD" | "">("");
  const [eSignature, setESignature] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDeliveryModes = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await getDeliveryModes();
        setDeliveryModes(response.body);
        if (response.body.length > 0) {
          setDeliveryModeCode(response.body[0].code);
        }
      } catch (err: unknown) {
        const errorObj = err as { response?: { body?: unknown } };
        setLoadError(
          extractServerErrorMessage(errorObj?.response?.body) ||
            "Unable to load delivery options.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeliveryModes();
  }, []);

  useEffect(() => {
    if (
      deliveryModeCode &&
      (paymentType === "ACCOUNT" || paymentType === "CARD") &&
      eSignature.trim()
    ) {
      onChange({
        deliveryModeCode,
        paymentType,
        eSignature: eSignature.trim(),
      });
    } else {
      onChange(null);
    }
  }, [deliveryModeCode, paymentType, eSignature, onChange]);

  return (
    <Tile data-testid="covetrus-order-info-form">
      {isLoading && <SpinningLoader />}
      <h4 className="margin-bottom-lg">
        We just need a little more info before you place your Covetrus order.
      </h4>

      {loadError && (
        <p className="text-error text-sm margin-bottom-md">{loadError}</p>
      )}

      {!loadError && (
        <div className={styles.columns}>
          <div
            className={styles.column}
            data-testid="covetrus-delivery-methods"
          >
            <div className="text-sm font-weight-bold margin-bottom-sm">
              1. Choose Delivery Method
            </div>
            {deliveryModes.map((mode) => (
              <RadioOption
                key={mode.code}
                name="covetrus-delivery-mode"
                value={mode.code}
                checked={deliveryModeCode === mode.code}
                label={mode.name}
                description={mode.description}
                onChange={setDeliveryModeCode}
              />
            ))}
          </div>

          <div className={styles.column} data-testid="covetrus-payment-types">
            <div className="text-sm font-weight-bold margin-bottom-sm">
              2. Choose Payment Type
            </div>
            <RadioOption
              name="covetrus-payment-type"
              value="ACCOUNT"
              checked={paymentType === "ACCOUNT"}
              label="Account"
              onChange={(value) => setPaymentType(value as "ACCOUNT" | "CARD")}
            />
            <RadioOption
              name="covetrus-payment-type"
              value="CARD"
              checked={paymentType === "CARD"}
              label="Card"
              onChange={(value) => setPaymentType(value as "ACCOUNT" | "CARD")}
            />
          </div>

          <div className={styles.column} data-testid="covetrus-esignature">
            <div className="text-sm font-weight-bold margin-bottom-sm">
              3. Sign for Your Order
            </div>
            <p className="text-xs text-secondary margin-bottom-sm">
              Type your full name in the field below.
            </p>
            <Input
              label=""
              value={eSignature}
              onChange={setESignature}
              width="100%"
              data-testid="covetrus-esignature-input"
            />
          </div>
        </div>
      )}
    </Tile>
  );
}
